import { useState } from 'react'

export type UserRole = 'company_user' | 'company_admin'

export interface UpdateUserRoleResponse {
  message: string
  user: {
    id: string
    email: string
    name: string
    role: UserRole
  }
}

export interface UseUpdateUserRoleResult {
  updateUserRole: (userId: string, newRole: UserRole) => Promise<UpdateUserRoleResponse | null>
  loading: boolean
  error: string | null
}

export function useUpdateUserRole(): UseUpdateUserRoleResult {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const updateUserRole = async (userId: string, newRole: UserRole): Promise<UpdateUserRoleResponse | null> => {
    try {
      setLoading(true)
      setError(null)

      // Get auth token
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch(`/api/company/users/${userId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role: newRole })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        throw new Error(errorData.error || `Failed to update user role (${response.status})`)
      }

      const data: UpdateUserRoleResponse = await response.json()
      return data
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update user role'
      setError(errorMessage)
      console.error('Error updating user role:', err)
      return null
    } finally {
      setLoading(false)
    }
  }

  return {
    updateUserRole,
    loading,
    error
  }
}
